import { FC, useEffect, useState } from 'react';

import { Alert, Button } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './ApiKeyWarning.module.scss';
import { axiosConfigAuth } from '../../api/middleware';
import { API_SCHOOL_API_KEY_STATUS } from '../../api/urls';
import { routeNames } from '../../constants/routes';
import { useTypedSelector } from '../../hooks/useTypedSelector';

/** Причина, по которой автовыгрузка GetCourse выключила школу (GET /school-api-key/status) */
type TApiKeyErrorType = 'invalid_key' | 'no_access' | 'wrong_domain' | 'unknown';

interface IApiKeyStatus {
  valid: boolean;
  error_type: TApiKeyErrorType | null;
  error_code: number | null;
  since: string | null;
  can_edit: boolean;
}

const ERROR_TEXTS: Record<TApiKeyErrorType, string> = {
  invalid_key: 'API-ключ GetCourse не принимается. Проверьте ключ в настройках школы.',
  no_access:
    'Ключ GetCourse рабочий, но у аккаунта GetCourse нет доступа к API. Проверьте тариф GetCourse или обратитесь в их поддержку.',
  wrong_domain: 'Домен школы в GetCourse не найден. Проверьте домен в настройках школы.',
  unknown: 'Ключ API GetCourse не работает. Проверьте ключ в настройках школы.',
};

const SINCE_FORMAT = 'DD.MM.YYYY HH:mm';

/**
 * Красная полоса над страницей, когда автовыгрузка GetCourse остановлена из-за ключа API.
 * Кнопку «Открыть настройки» видят те, кто может исправить ключ (can_edit),
 * остальным предлагается обратиться к владельцу школы.
 */
const ApiKeyWarning: FC = () => {
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const location = useLocation();
  const navigate = useNavigate();
  const [ status, setStatus ] = useState<IApiKeyStatus | null>(null);

  const schoolId = selectedSchool?.id;

  // Перезапрос при смене школы и страницы: после исправления ключа в настройках полоса пропадает сама
  useEffect(() => {
    if (!schoolId) {
      setStatus(null);
      return undefined;
    }

    let isActual = true;

    axios
      .get(API_SCHOOL_API_KEY_STATUS, axiosConfigAuth({}, { params: { school_id: schoolId } }))
      .then((response) => {
        if (isActual) {
          setStatus(response.data?.data ?? null);
        }
      })
      .catch((error) => {
        // Полоса вспомогательная: при ошибке запроса страницу не блокируем
        console.error('Не удалось получить состояние ключа GetCourse:', error);
        if (isActual) {
          setStatus(null);
        }
      });

    return () => {
      isActual = false;
    };
  }, [ schoolId, location.pathname ]);

  if (!status || status.valid) {
    return null;
  }

  const errorText = ERROR_TEXTS[status.error_type ?? 'unknown'] ?? ERROR_TEXTS.unknown;
  const since = status.since ? dayjs(status.since).format(SINCE_FORMAT) : null;
  const stoppedText = since
    ? `Данные школы из GetCourse не обновляются с ${since}.`
    : 'Данные школы из GetCourse не обновляются.';
  const isSettingsPage = location.pathname === routeNames.schoolEdit;

  return (
    <Alert
      className={styles.warning}
      type="error"
      banner
      message={`${stoppedText} ${errorText}`}
      action={
        status.can_edit ? (
          !isSettingsPage && (
            <Button size="small" danger onClick={() => navigate(routeNames.schoolEdit)}>
              Открыть настройки
            </Button>
          )
        ) : (
          <span>Обратитесь к владельцу школы</span>
        )
      }
    />
  );
};

export default ApiKeyWarning;
